import { BACKEND_URL_FROM_ENV } from "./backend-url.generated";

const EXTENSION_SOURCE = "threshold-free-gift-checkout-sync";
const EXTENSION_TARGET = "purchase.checkout.block.render";
const THRESHOLD_ATTRIBUTE_KEY = "_threshold_gift";
const THRESHOLD_ATTRIBUTE_VALUE = "free";
const SYNC_RETRY_DELAY_MS = 350;

function getCheckoutApi(args) {
  const candidates = [...args, globalThis.shopify].filter(Boolean);

  return candidates.find(
    (candidate) =>
      typeof candidate.applyCartLinesChange === "function" &&
      candidate.lines &&
      candidate.shop,
  );
}

function getSignalValue(signal, fallback = undefined) {
  return signal?.value ?? signal?.current ?? fallback;
}

function getShopDomain(api) {
  return api?.shop?.myshopifyDomain || "";
}

function getCheckoutToken(api) {
  return getSignalValue(api?.checkoutToken, null) || null;
}

function createThresholdAttribute() {
  return { key: THRESHOLD_ATTRIBUTE_KEY, value: THRESHOLD_ATTRIBUTE_VALUE };
}

function normalizeAttributes(attributes) {
  return Array.isArray(attributes)
    ? attributes.flatMap((attribute) => {
        if (!attribute?.key) return [];

        return [
          {
            key: String(attribute.key),
            value: String(attribute.value ?? ""),
          },
        ];
      })
    : [];
}

function isThresholdGiftAttribute(attribute) {
  return (
    attribute?.key === THRESHOLD_ATTRIBUTE_KEY &&
    String(attribute?.value || "").toLowerCase() === THRESHOLD_ATTRIBUTE_VALUE
  );
}

function isThresholdGiftLine(line) {
  return normalizeAttributes(line?.attributes).some(isThresholdGiftAttribute);
}

function withThresholdAttribute(attributes) {
  const nextAttributes = normalizeAttributes(attributes).filter(
    (attribute) => attribute.key !== THRESHOLD_ATTRIBUTE_KEY,
  );

  nextAttributes.push(createThresholdAttribute());

  return nextAttributes;
}

function getLineAmount(line) {
  const amount = Number(line?.cost?.totalAmount?.amount);
  return Number.isFinite(amount) ? amount : 0;
}

function getThresholdBaseAmount(cartLines) {
  return cartLines
    .filter((line) => !isThresholdGiftLine(line))
    .reduce((sum, line) => sum + getLineAmount(line), 0);
}

function getCartLineSignature(cartLines) {
  return cartLines
    .map((line) => {
      const attributes = normalizeAttributes(line.attributes)
        .map((attribute) => `${attribute.key}:${attribute.value}`)
        .sort()
        .join("|");

      return `${line.id}:${line.merchandise?.id}:${line.quantity}:${getLineAmount(line)}:${attributes}`;
    })
    .join(",");
}

async function logEvent(api, level, eventType, message, payload = {}) {
  const backendUrl = String(BACKEND_URL_FROM_ENV || "")
    .trim()
    .replace(/\/$/, "");
  const shop = getShopDomain(api);

  if (!backendUrl || !shop) return;

  try {
    await fetch(`${backendUrl}/api/app-event-log`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        shop,
        source: EXTENSION_SOURCE,
        extensionTarget: EXTENSION_TARGET,
        level,
        eventType,
        message,
        checkoutToken: getCheckoutToken(api),
        payload,
      }),
    });
  } catch (error) {
    console.error("Threshold gift log failed", error);
  }
}

async function fetchThresholdGiftConfig(api) {
  const backendUrl = String(BACKEND_URL_FROM_ENV || "")
    .trim()
    .replace(/\/$/, "");
  const shop = getShopDomain(api);

  if (!backendUrl || !shop) {
    return [];
  }

  const response = await fetch(
    `${backendUrl}/api/threshold-gift-config?shop=${encodeURIComponent(shop)}`,
  );

  if (!response.ok) {
    console.error(
      "Failed to load threshold gift config",
      await response.text(),
    );
    return [];
  }

  const json = await response.json();

  return Array.isArray(json?.rules)
    ? json.rules.flatMap((rule) => {
        const thresholdAmount = Number(rule?.thresholdAmount);
        const giftVariantId =
          typeof rule?.giftVariantId === "string" ? rule.giftVariantId : "";
        const giftQuantity = Number(rule?.giftQuantity);

        return Number.isFinite(thresholdAmount) &&
          thresholdAmount > 0 &&
          giftVariantId
          ? [
              {
                thresholdAmount,
                giftVariantId,
                giftQuantity:
                  Number.isFinite(giftQuantity) && giftQuantity > 0
                    ? Math.trunc(giftQuantity)
                    : 1,
              },
            ]
          : [];
      })
    : [];
}

function buildSyncChanges(cartLines, rules) {
  const managedGiftVariantIds = new Set(
    rules.map((rule) => rule.giftVariantId).filter(Boolean),
  );

  if (!rules.length) {
    return {
      baseAmount: getThresholdBaseAmount(cartLines),
      changes: cartLines.filter(isThresholdGiftLine).map((line) => ({
        type: "removeCartLine",
        id: line.id,
        quantity: line.quantity,
      })),
    };
  }

  const baseAmount = getThresholdBaseAmount(cartLines);
  const desiredQuantitiesByGift = rules.reduce((result, rule) => {
    if (baseAmount > rule.thresholdAmount) {
      const giftQuantity =
        Number.isFinite(rule.giftQuantity) && rule.giftQuantity > 0
          ? Math.trunc(rule.giftQuantity)
          : 1;

      result[rule.giftVariantId] =
        (result[rule.giftVariantId] ?? 0) + giftQuantity;
    }

    return result;
  }, {});
  const managedGiftLinesByVariant = {};
  const unmanagedGiftLinesByVariant = {};

  for (const line of cartLines) {
    const variantId = line.merchandise?.id;
    if (!variantId || !managedGiftVariantIds.has(variantId)) continue;

    const collection = isThresholdGiftLine(line)
      ? managedGiftLinesByVariant
      : unmanagedGiftLinesByVariant;

    collection[variantId] ??= [];
    collection[variantId].push({
      id: line.id,
      quantity: line.quantity,
      attributes: line.attributes ?? [],
    });
  }

  const changes = [];

  for (const giftVariantId of managedGiftVariantIds) {
    const desiredQuantity = desiredQuantitiesByGift[giftVariantId] ?? 0;
    const managedGiftLines = managedGiftLinesByVariant[giftVariantId] ?? [];
    const unmanagedGiftLines = unmanagedGiftLinesByVariant[giftVariantId] ?? [];
    const totalManagedQuantity = managedGiftLines.reduce(
      (sum, line) => sum + line.quantity,
      0,
    );

    if (desiredQuantity <= 0) {
      for (const giftLine of managedGiftLines) {
        changes.push({
          type: "removeCartLine",
          id: giftLine.id,
          quantity: giftLine.quantity,
        });
      }
      continue;
    }

    if (managedGiftLines.length === 0) {
      const [primaryUnmanagedGiftLine, ...extraUnmanagedGiftLines] =
        unmanagedGiftLines;

      if (primaryUnmanagedGiftLine) {
        changes.push({
          type: "updateCartLine",
          id: primaryUnmanagedGiftLine.id,
          quantity: desiredQuantity,
          attributes: withThresholdAttribute(
            primaryUnmanagedGiftLine.attributes,
          ),
        });

        for (const extraGiftLine of extraUnmanagedGiftLines) {
          changes.push({
            type: "removeCartLine",
            id: extraGiftLine.id,
            quantity: extraGiftLine.quantity,
          });
        }

        continue;
      }

      changes.push({
        type: "addCartLine",
        merchandiseId: giftVariantId,
        quantity: desiredQuantity,
        attributes: [createThresholdAttribute()],
      });
      continue;
    }

    const [primaryGiftLine, ...extraGiftLines] = managedGiftLines;

    if (totalManagedQuantity !== desiredQuantity) {
      changes.push({
        type: "updateCartLine",
        id: primaryGiftLine.id,
        quantity: desiredQuantity,
        attributes: withThresholdAttribute(primaryGiftLine.attributes),
      });
    }

    for (const extraGiftLine of extraGiftLines) {
      changes.push({
        type: "removeCartLine",
        id: extraGiftLine.id,
        quantity: extraGiftLine.quantity,
      });
    }
  }

  return { baseAmount, changes };
}

export default function (...args) {
  const api = getCheckoutApi(args);

  if (!api) {
    console.error("Threshold gift extension could not access Checkout API.");
    return;
  }

  let rules = [];
  let configLoaded = false;
  let isSyncing = false;
  let cartNeedsSync = false;
  let lastSignature = "";
  let retryTimer;
  let syncTimer;

  const scheduleSync = (delay = 0) => {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(() => {
      void syncThresholdGiftLines();
    }, delay);
  };

  const setSyncState = ({ syncing, needsSync }) => {
    isSyncing = syncing;
    cartNeedsSync = needsSync;
  };

  async function syncThresholdGiftLines() {
    if (!configLoaded || isSyncing) return;

    const cartLines = getSignalValue(api.lines, []);
    const signature = `${JSON.stringify(rules)}::${getCartLineSignature(cartLines)}`;

    if (signature === lastSignature && !cartNeedsSync) return;
    lastSignature = signature;

    const { baseAmount, changes } = buildSyncChanges(cartLines, rules);

    if (!changes.length) {
      setSyncState({ syncing: false, needsSync: false });
      return;
    }

    setSyncState({ syncing: true, needsSync: true });

    let failed = false;

    try {
      for (const change of changes) {
        const result = await api.applyCartLinesChange(change);

        if (result.type === "error") {
          failed = true;
          console.error("Threshold gift sync failed", result.message);
          void logEvent(
            api,
            "error",
            "threshold_gift_sync_error",
            result.message,
            {
              change,
              baseAmount,
              extensionTarget: EXTENSION_TARGET,
            },
          );
          break;
        }

        const eventDetails =
          change.type === "addCartLine"
            ? {
                eventType: "threshold_gift_auto_added",
                message: "Threshold gift was auto-added to checkout.",
                variantId: change.merchandiseId,
                quantity: change.quantity,
              }
            : change.type === "removeCartLine"
              ? {
                  eventType: "threshold_gift_auto_removed",
                  message: "Threshold gift was auto-removed from checkout.",
                  lineId: change.id,
                  quantity: change.quantity,
                }
              : change.type === "updateCartLine"
                ? {
                    eventType: "threshold_gift_qty_updated",
                    message:
                      "Threshold gift quantity was updated automatically.",
                    lineId: change.id,
                    quantity: change.quantity,
                  }
                : null;

        if (eventDetails) {
          void logEvent(
            api,
            "info",
            eventDetails.eventType,
            eventDetails.message,
            {
              ...eventDetails,
              baseAmount,
              extensionTarget: EXTENSION_TARGET,
            },
          );
        }
      }
    } finally {
      setSyncState({ syncing: false, needsSync: failed });
      clearTimeout(retryTimer);
      retryTimer = setTimeout(() => {
        if (!failed) {
          setSyncState({ syncing: false, needsSync: false });
        }
        scheduleSync();
      }, SYNC_RETRY_DELAY_MS);
    }
  }

  api.lines?.subscribe?.(() => scheduleSync());

  void fetchThresholdGiftConfig(api)
    .then((nextRules) => {
      rules = nextRules;
      configLoaded = true;
      scheduleSync();
    })
    .catch((error) => {
      console.error("Failed to fetch threshold gift config", error);
      rules = [];
      configLoaded = true;
      scheduleSync();
    });
}
